/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Attributes from "./Attributes";

class Armor {
	public name: string;
	public type: 'light' | 'medium' | 'heavy' | 'special' | 'minAc';
	public ac: number;
	public minStr: number;
	public stealth: 'yes' | 'no';

	constructor(name: string,
	            type: 'light' | 'medium' | 'heavy' | 'special' | 'minAc',
	            ac: number,
	            minStr: number,
	            stealth: 'yes' | 'no') {
		this.name = name;
		this.type = type;
		this.ac = ac;
		this.minStr = minStr;
		this.stealth = stealth;
	}
	static equals(a: Armor | null, b: Armor | null): boolean {
		if (a == null && b == null) return true;
		if (a == null || b == null) return false;
		return (a.name    === b.name   &&
				a.type    === b.type   &&
				a.ac      === b.ac     &&
			    a.minStr  === b.minStr &&
			    a.stealth === b.stealth);
	}

    static armors: Armor[] = [
		new Armor("",                "special", 10,  0, 'yes'),
		new Armor("Natural Armor",   "special", 12,  0, 'yes'),
		new Armor("Mage Armor",      "special", 13,  0, 'yes'),
		new Armor("Barkskin",        "minAc",   16,  0, 'yes'),
		new Armor("Padded Armor",    "light",   11,  0,  'no'),
		new Armor("Leather Armor",   "light",   11,  0, 'yes'),
		new Armor("Studded Leather", "light",   12,  0, 'yes'),
		new Armor("Hide Armor",      "medium",  12,  0, 'yes'),
		new Armor("Chain Shirt",     "medium",  13,  0, 'yes'),
		new Armor("Scale Mail",      "medium",  14,  0,  'no'),
		new Armor("Breastplate",     "medium",  14,  0, 'yes'),
		new Armor("Half Plate",      "medium",  15,  0,  'no'),
		new Armor("Ring Mail",       "heavy",   14,  0,  'no'),
		new Armor("Chain Mail",      "heavy",   16, 13,  'no'),
		new Armor("Splint",          "heavy",   17, 15,  'no'),
		new Armor("Plate",           "heavy",   18, 15,  'no')
	];

    static getArmorNames(): string[] {
        return this.armors.map(armor => armor.name);
    }

    static getGearNames(): string[] {
        return [...this.armors.filter(armor => armor.type != 'special' && armor.type != 'minAc').map(armor => armor.name), 'Shield'];
    }

	static getArmorClass(armorName: string): number {
		return this.getArmor(armorName)?.ac || 10;
	}
  	static getArmor(armorName: string): Armor | undefined {
		if (armorName == null) {
			armorName = '';
		}
		armorName = armorName.replace(/,? *shield,? */gi, ' ').trim();
		const armor = this.armors.find(a => a.name.toLowerCase() === armorName.toLowerCase());
		const clone = armor && structuredClone(armor);
		return clone || undefined;
	}

	static getAcFromDexAndArmorDesc(dexLevel: number, armorName: string): number {
		const dexBonus = Attributes.getBonus(dexLevel);

		let shield = false;
		if (armorName.toLowerCase() === 'shield') {
		  shield = true;
		  armorName = '';
		} else if (armorName && armorName.toLowerCase().includes(', shield')) {
		  armorName = armorName.replace(/, shield/gi, '').trim() || '';
		  shield = true;
		}
	
		const armor = this.getArmor(armorName) || Armor.armors[0];

		let ac = armor.ac || 10;
		if (ac == 10 || armor.type === 'light' || armor.type === 'special') {
		  ac += dexBonus;
		} else if (armor.type === 'medium') {
		  ac += Math.min(2, dexBonus);
		} else if (armor.type == 'minAc') {
		  ac = 10 + dexBonus;
		}
		if (shield) {
		  ac += 2;
		}
		if (armor.type == 'minAc') {
			ac = Math.max(ac, armor.ac);
		}
		return ac;
	}

	static buildAC = (type: Armor | null, dex: number, shield: boolean): number => {
		let newAC = type?.ac ?? 10;
	
		// Add DEX bonus, based on armor type:
		newAC += Armor.buildDexBonus(type?.type || null, dex);
		if (type?.type === 'minAc') {
		  newAC = 10 + Attributes.getBonus(dex);
		}
		
		// shield bonus:
		if (shield) {
		  newAC += 2;
		}
	
		// check for minAC type (i.e. barkskin)
		if (type?.type === 'minAc') {
		  newAC = Math.max(newAC, type.ac);
		}
		return newAC;
	}
	
	static buildDexBonus = (type: 'light' | 'medium' | 'heavy' | 'special' | 'minAc' | null, dex: number): number => {
		if (type === 'heavy') {
		  return 0;
		}
		const dexBonus = Attributes.getBonus(dex);
		if (type === 'medium') {
		  return Math.min(2, dexBonus);
		}
		return dexBonus;
	}
	
}

export default Armor;